import { useCallback, useEffect, useState } from 'react';

/**
 * The height an element has left before the bottom of the window.
 *
 * A reporting console is a fixed frame with a scrolling table inside it, not a document that grows
 * downwards — otherwise the rows push the parameters off the top, the pager ends up a screen below
 * the last row, and "how much of this report can I see" depends on how many rows came back.
 *
 * Measured rather than hard-coded: the app shell's header, the page padding and this page's own
 * chrome all have sizes of their own, and a `calc(100vh - 312px)` somewhere in a stylesheet is a
 * number that is wrong the first time any of them changes.
 *
 * Returns `undefined` below the given breakpoint (and while measuring), where the page should simply
 * flow — a fixed height on a phone is how a layout ends up with a table that cannot be scrolled to.
 */
export function useFillHeight(
  el: HTMLElement | null,
  { gutter = 24, minHeight = 360, minWidth = 1024 }: { gutter?: number; minHeight?: number; minWidth?: number } = {},
): number | undefined {
  const [height, setHeight] = useState<number>();

  const measure = useCallback(() => {
    if (!el || typeof window === 'undefined') return;
    if (window.innerWidth < minWidth) { setHeight(undefined); return; }
    // Document-relative, so a page that happens to be scrolled does not measure a taller frame than
    // it has: the frame is sized for where it sits in the document, not for where it is on screen.
    const docTop = el.getBoundingClientRect().top + window.scrollY;
    setHeight(Math.max(minHeight, Math.round(window.innerHeight - docTop - gutter)));
  }, [el, gutter, minHeight, minWidth]);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    // The chrome ABOVE the frame can change height on its own — a filter chip row appears, a long
    // report name wraps — and the frame has to give that height back rather than run off the screen.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro && el?.parentElement) ro.observe(el.parentElement);
    return () => { window.removeEventListener('resize', measure); ro?.disconnect(); };
  }, [measure, el]);

  return height;
}
